import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Wallet, Loader2, CheckCircle2, Landmark, Zap, Banknote, Download,
} from 'lucide-react';

function RecipientStatusBadge({ status }) {
  const map = {
    pending: { color: 'bg-amber-100 text-amber-800', label: 'Pending' },
    fast_comm_paid: { color: 'bg-yellow-100 text-yellow-800', label: 'Fast Comm Paid' },
    paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
    unclaimed: { color: 'bg-gray-100 text-gray-600', label: 'Unclaimed' },
  };
  const s = map[status] || map.pending;
  return <Badge className={`${s.color} text-xs`}>{s.label}</Badge>;
}

export function PaymentTrackerDialog({
  open, onOpenChange, data, loading,
  onMarkDeveloperFunded, onMarkRecipientPaid, onDownloadReceipt,
  devFundOpen, setDevFundOpen, devFundForm, setDevFundForm,
  payRecipientOpen, setPayRecipientOpen, payForm, setPayForm,
  payLoading,
}) {
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="payment-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-teal-600" /> Payment Tracker - {data?.claim_ref}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>
          ) : data && (
            <div className="space-y-4">
              {/* Summary Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Net Commission</p>
                  <p className="font-mono font-bold text-blue-700">RM {(data.net_commission || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="font-mono font-bold text-amber-700">RM {(data.summary?.total_pending || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-gray-400">{data.summary?.pending_count || 0} recipients</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Paid</p>
                  <p className="font-mono font-bold text-green-700">RM {(data.summary?.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-gray-400">{data.summary?.paid_count || 0} recipients</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ background: data.developer_funded_at ? '#ecfdf5' : '#fef3c7' }}>
                  <p className="text-xs text-gray-500">Developer Status</p>
                  <Badge className={data.developer_funded_at ? 'bg-teal-500 text-white' : 'bg-amber-400 text-white'}>
                    {data.developer_funded_at ? 'Funded' : 'Pending'}
                  </Badge>
                </div>
              </div>

              {!data.developer_funded_at && ['approved_pending_fund', 'approved'].includes(data.status) && (
                <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={() => { setDevFundOpen(true); setDevFundForm({ payment_date: new Date().toISOString().slice(0, 10), payment_ref: '', payment_amount: data.net_commission || '' }); }} data-testid="mark-developer-funded-btn">
                  <Landmark className="h-4 w-4 mr-2" /> Mark Developer Funded
                </Button>
              )}

              {data.developer_funded_at && (
                <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    <div>
                      <p className="text-sm font-medium text-teal-800">Developer Funded</p>
                      <p className="text-xs text-gray-500">Ref: {data.developer_payment_ref} | RM {(data.developer_payment_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">{data.developer_funded_at?.split('T')[0]}</span>
                </div>
              )}

              {data.fast_comm === '100' && data.agent_wallet_amount > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1"><Zap className="h-4 w-4 text-yellow-600" /><span className="text-sm font-medium text-yellow-800">Fast Comm Active</span></div>
                  <p className="text-xs text-gray-600">Agent advance: RM {data.agent_wallet_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} (Fee: RM {(data.fast_comm_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} + SST: RM {(data.fast_comm_sst || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })})</p>
                </div>
              )}

              {/* Recipients Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">Pool</TableHead>
                      <TableHead className="text-xs">Recipient</TableHead>
                      <TableHead className="text-xs text-right">Rate</TableHead>
                      <TableHead className="text-xs text-right">Net (RM)</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.recipients || []).map((r, idx) => (
                      <TableRow key={idx} className={r.payment_status === 'paid' ? 'bg-green-50/50' : r.payment_status === 'fast_comm_paid' ? 'bg-yellow-50/50' : ''}>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className={r.pool === 'sam_rumah' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-blue-50 text-blue-700 border-blue-300'}>
                            {r.pool === 'sam_rumah' ? 'SR' : 'SP'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-gray-200 text-gray-700 text-xs">{r.rank_display}</Badge>
                            <span className="text-sm font-medium">{r.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.rate_percentage}%</TableCell>
                        <TableCell className="text-right font-mono text-sm font-semibold">
                          {(r.nett_comm || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell><RecipientStatusBadge status={r.payment_status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {r.payment_status === 'pending' && (data.developer_funded_at || data.fast_comm === '100') && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50" onClick={() => { setPayRecipientOpen(r.recipient_idx); setPayForm({ payment_method: 'bank_transfer', payment_ref: '', payment_date: new Date().toISOString().slice(0, 10) }); }} data-testid={`mark-paid-btn-${r.recipient_idx}`}>
                                <Banknote className="h-3 w-3 mr-1" /> Pay
                              </Button>
                            )}
                            {(r.payment_status === 'paid' || r.payment_status === 'fast_comm_paid') && r.slip_filename && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onDownloadReceipt(data.claim_id, r.recipient_idx)} data-testid={`download-receipt-btn-${r.recipient_idx}`}>
                                <Download className="h-3 w-3 mr-1" /> Receipt
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Developer Funded Dialog */}
      <Dialog open={devFundOpen} onOpenChange={setDevFundOpen}>
        <DialogContent className="max-w-md" data-testid="dev-fund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-teal-600" /> Mark Developer Funded</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Payment Date</Label><Input type="date" value={devFundForm.payment_date} onChange={e => setDevFundForm(p => ({ ...p, payment_date: e.target.value }))} data-testid="dev-fund-date" /></div>
            <div className="space-y-2"><Label>Bank Reference</Label><Input placeholder="e.g. DEV-20260401" value={devFundForm.payment_ref} onChange={e => setDevFundForm(p => ({ ...p, payment_ref: e.target.value }))} data-testid="dev-fund-ref" /></div>
            <div className="space-y-2"><Label>Amount Received (RM)</Label><Input type="number" value={devFundForm.payment_amount} onChange={e => setDevFundForm(p => ({ ...p, payment_amount: e.target.value }))} data-testid="dev-fund-amount" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDevFundOpen(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={onMarkDeveloperFunded} disabled={payLoading} data-testid="confirm-dev-fund">
              {payLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Landmark className="h-4 w-4 mr-1" />} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Recipient Dialog */}
      <Dialog open={payRecipientOpen !== null} onOpenChange={() => setPayRecipientOpen(null)}>
        <DialogContent className="max-w-md" data-testid="pay-recipient-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Banknote className="h-5 w-5 text-green-600" /> Pay Recipient #{payRecipientOpen}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Payment Method</Label><Input value={payForm.payment_method} onChange={e => setPayForm(p => ({ ...p, payment_method: e.target.value }))} data-testid="pay-method" /></div>
            <div className="space-y-2"><Label>Bank Reference</Label><Input value={payForm.payment_ref} onChange={e => setPayForm(p => ({ ...p, payment_ref: e.target.value }))} data-testid="pay-ref" /></div>
            <div className="space-y-2"><Label>Payment Date</Label><Input type="date" value={payForm.payment_date} onChange={e => setPayForm(p => ({ ...p, payment_date: e.target.value }))} data-testid="pay-date" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayRecipientOpen(null)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={onMarkRecipientPaid} disabled={payLoading} data-testid="confirm-pay">
              {payLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Banknote className="h-4 w-4 mr-1" />} Pay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
